import FormatBoldIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicIcon from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useCallback } from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import type { ReportField } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import { isAggregationBased, isDimensionBased } from "../../../../../utilities/fieldsHelper.ts";
import SwitchField from "../../../../common/SwitchField.tsx";
interface Props {
  field: ReportField;
  readonlyMode: boolean;
}

export default function TextField({ field, readonlyMode }: Props) {
  const dispatch = useAppDispatch();

  const onChangeShowTotals = useCallback(
    (showTotals: boolean) => {
      if (isDimensionBased(field) || isAggregationBased(field)) {
        const style = !field.config.style ? {} : structuredClone(field.config.style);
        style.showTotals = showTotals;
        dispatch(fieldsStateActions.updateRowConfig({ field, changes: { style } }));
      }
    },
    [field, dispatch]
  );

  const onUpdateFontStyle = useCallback(
    (fontStyle: string[]) => {
      if (isDimensionBased(field) || isAggregationBased(field)) {
        const style = !field.config.style ? {} : structuredClone(field.config.style);
        style.fontStyle = fontStyle;
        dispatch(fieldsStateActions.updateRowConfig({ field, changes: { style } }));
      }
    },
    [field, dispatch]
  );

  return (
    <Stack sx={{ borderBottom: "1px dashed", borderBottomColor: "divider", width: "100%" }}>
      <Typography variant="subtitle2" color="textPrimary" sx={{ fontWeight: 500 }}>
        {field.config.customLabel || field.meta.caption}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", py: ".7rem", gap: 1 }}>
        <Grid container sx={{ display: "flex", flex: 1, width: "100%" }}>
          <Grid container sx={{ alignItems: "center", width: "100%" }}>
            <Typography sx={{ color: "text.secondary" }}>Text</Typography>
            <HorizontalFill />
            <ToggleButtonGroup
              value={field.config.style?.fontStyle}
              sx={{ ".MuiButtonBase-root": { border: "none", py: 0, px: 0.5, "&.Mui-disabled": { opacity: 0.5 } } }}
              onChange={(_, value: string[]) => onUpdateFontStyle(value)}
              disabled={readonlyMode}
            >
              <ToggleButton value={"bold"}>
                <FormatBoldIcon />
              </ToggleButton>
              <ToggleButton value={"italic"}>
                <FormatItalicIcon />
              </ToggleButton>
              <ToggleButton value={"underlined"}>
                <FormatUnderlinedIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        <SwitchField
          label="Show Totals"
          checked={field.config.style?.showTotals === true}
          onChange={onChangeShowTotals}
          typographyProps={{ sx: { color: "text.secondary" } }}
          disabled={readonlyMode}
        />
      </Box>
    </Stack>
  );
}
